"use client";

import React, { createContext, useContext, useMemo, useRef } from "react";
import pdf from "../../assets/img/pdf.png";
import { Block } from "./Block";

/* -------------------------------------------------------------------------------------------------
 * Default blocks and modifiers components
 * -----------------------------------------------------------------------------------------------*/

const defaultComponents = {
  blocks: {
    paragraph: (props) => <>{props.children}</>,
    quote: (props) => <blockquote>{props.children}</blockquote>,
    code: (props) => (
      <pre>
        <code>{props.plainText}</code>
      </pre>
    ),
    heading: ({ level, children }) => {
      switch (level) {
        case 1:
          return <h1>{children}</h1>;
        case 2:
          return <h2>{children}</h2>;
        case 3:
          return <h3>{children}</h3>;
        case 4:
          return <h4>{children}</h4>;
        case 5:
          return <h5>{children}</h5>;
        case 6:
          return <h6>{children}</h6>;
      }
    },
    link: (props) => (
      <tr>
        <td>
          <p>{props.children}</p>
        </td>
        <td>
          <a
            href={props.url}
            className="d-flex flex-column align-items-center"
            target="_blank"
            rel="noreferrer"
          >
            <img
              alt="PDF file"
              title="PDF file"
              src={pdf}
              className="mb-5"
              style={{ width: "30px", height: "30px", marginLeft: "10px" }}
            />
          </a>
        </td>
      </tr>
    ),
    list: (props) => {
      if (props.format === "ordered") {
        return <ol>{props.children}</ol>;
      }
      return (
        <ul>
          {React.Children.map(props.children, (child, index) => (
            <li key={index}>{child}</li>
          ))}
        </ul>
      );
    },
    "list-item": (props) => <li>{props.children}</li>,
    image: (props) => (
      <img
        style={{ marginBottom: "20px" }}
        src={props.image.url}
        alt={props.image.alternativeText || undefined}
        title={props.image.alternativeText || undefined}
      />
    ),
  },
  modifiers: {
    bold: (props) => <strong>{props.children}</strong>,
    italic: (props) => <em>{props.children}</em>,
    underline: (props) => <u>{props.children}</u>,
    strikethrough: (props) => <del>{props.children}</del>,
    code: (props) => <code>{props.children}</code>,
  },
  missingBlockTypes: [],
  missingModifierTypes: [],
};

/* -------------------------------------------------------------------------------------------------
 * Context to pass blocks and inline components to the nested components
 * -----------------------------------------------------------------------------------------------*/

const ComponentsContext = createContext(defaultComponents);

const ComponentsProvider = ({ children, value = defaultComponents }) => {
  const memoizedValue = useMemo(() => value, [value]);

  return (
    <ComponentsContext.Provider value={memoizedValue}>
      {children}
    </ComponentsContext.Provider>
  );
};

function useComponentsContext() {
  return useContext(ComponentsContext);
}

/* -------------------------------------------------------------------------------------------------
 * BlocksRenderer
 * -----------------------------------------------------------------------------------------------*/

const BlocksRenderer = (props) => {
  const blocks = {
    ...defaultComponents.blocks,
    ...props.blocks,
  };

  const modifiers = {
    ...defaultComponents.modifiers,
    ...props.modifiers,
  };

  const missingBlockTypes = useRef([]);
  const missingModifierTypes = useRef([]);

  return (
    <ComponentsProvider
      value={{
        blocks,
        modifiers,
        missingBlockTypes: missingBlockTypes.current,
        missingModifierTypes: missingModifierTypes.current,
      }}
    >
      {props.content.map((content, index) => (
        <Block content={content} key={index} />
      ))}
    </ComponentsProvider>
  );
};

/* -------------------------------------------------------------------------------------------------
 * Exports
 * -----------------------------------------------------------------------------------------------*/

export { ComponentsProvider, useComponentsContext, BlocksRenderer };
